import React, { useEffect, useRef, useState } from 'react'

import 'react-phone-number-input/style.css'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'

import Label from '@components/labels/Label'
import type { validatorFunc } from '@/utils/validatorProvider'
import Validator from '@/pages/form/validator/Validator'

import './LabelAndTextInput.css'

// PhoneInput expects an E.164 value (e.g. "+33600000000"). Older/seed data may
// hold a national number (e.g. "0600000000"), which makes the library warn and
// renders incorrectly. Convert such values to E.164 when possible.
function toE164(value?: string): string | undefined {
	if (!value || value.startsWith('+')) return value
	try {
		return parsePhoneNumber(value, 'FR')?.number ?? value
	} catch {
		return value
	}
}

interface LabelAndPhoneInputProps {
	id: string
	type?: string
	label: string
	name?: string
	placeholder?: string
	value?: string
	required?: boolean
	readOnly?: Object
	onChange: (value: string | undefined) => void
	style?: React.CSSProperties
	autocomplete?: string
}

function LabelAndPhoneInput(props: LabelAndPhoneInputProps) {
	const [errors, setErrors] = useState<string[]>([])
	// useRef, not createRef: see LabelAndTextInput.
	const ref = useRef<any>(null)

	const { value, onChange } = props
	const normalizedValue = toE164(value)

	// Push the normalised value back up so stored state stays E.164 too.
	useEffect(() => {
		if (normalizedValue !== value) {
			onChange(normalizedValue)
		}
	}, [normalizedValue, value, onChange])

	const handleChange = (phone: string | undefined) => {
		props.onChange(phone)
	}

	// An optional number may be left out, but one that is given must be valid.
	const validator: validatorFunc = value => {
		if (!props.required && !value) {
			return []
		}
		if (!isValidPhoneNumber(value, 'FR')) {
			return [{ valid: false, error: 'Numéro de téléphone incorrect' }]
		}
		return [{ valid: true, error: '' }]
	}

	const handleError = (errors: string[]) => {
		setErrors(errors)
	}

	return (
		<Validator
			id={props.id}
			validator={validator}
			reference={ref}
			onError={handleError}
		>
			<div style={props.style} className='LabelAndTextInput'>
				<Label htmlFor={props.id}>{props.label}</Label>
				<PhoneInput
					id={props.id}
					placeholder={props.placeholder}
					value={normalizedValue}
					name={props.name ?? props.id}
					defaultCountry='FR'
					onChange={handleChange}
					autoComplete={props.autocomplete ?? 'tel'}
					ref={ref}
				/>
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}
export default LabelAndPhoneInput
